import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ServerService } from './ServerService';

export type AvisoAtualizacaoTipoVM =
  'novidade'
  | 'melhoria'
  | 'correcao'
  | 'comunicado_operacional'
  | 'indisponibilidade_suporte'
  | 'manutencao'
  | 'importante';
export type AvisoAtualizacaoStatusVM = 'rascunho' | 'publicado' | 'arquivado';
export type AvisoAtualizacaoPrioridadeVM = 'baixa' | 'normal' | 'alta';

export interface ImagemAvisoAtualizacaoVM {
  linkImagem: string;
  ordem: number;
}

export interface AvisoAtualizacaoFiltroListagemVM {
  busca?: string;
  status?: AvisoAtualizacaoStatusVM;
  inicio?: number;
  quantidade?: number;
}

export interface AvisoAtualizacaoResumoVM {
  id: number;
  titulo: string;
  resumo: string;
  tipo: AvisoAtualizacaoTipoVM;
  status: AvisoAtualizacaoStatusVM;
  prioridade: AvisoAtualizacaoPrioridadeVM;
  inicioExibicao: Date | null;
  fimExibicao: Date | null;
  global: boolean;
  exigirConfirmacao: boolean;
  exibirUmaVezPorUsuario: boolean;
  empresaIds: number[];
  criadoEm: Date | null;
  alteradoEm: Date | null;
  totalVisualizacoes: number;
  totalConfirmacoes: number;
  totalAdiamentos: number;
  totalExibicoes: number;
}

export interface AvisoAtualizacaoDetalheVM extends AvisoAtualizacaoResumoVM {
  imagens: ImagemAvisoAtualizacaoVM[];
  conteudo: string;
  textoAcao: string;
  linkAcao: string;
  usuarioCriacaoId: number | null;
  usuarioAlteracaoId: number | null;
}

export interface AvisoAtualizacaoAtivoVM {
  imagens?: ImagemAvisoAtualizacaoVM[];
  id: number;
  titulo: string;
  resumo: string;
  conteudo: string;
  tipo: AvisoAtualizacaoTipoVM;
  prioridade: AvisoAtualizacaoPrioridadeVM;
  inicioExibicao: Date | null;
  fimExibicao: Date | null;
  textoAcao: string;
  linkAcao: string;
  exigirConfirmacao: boolean;
  exibirUmaVezPorUsuario: boolean;
  global: boolean;
  empresaIds: number[];
}

export interface AvisoAtualizacaoEdicaoVM {
  imagens?: ImagemAvisoAtualizacaoVM[];
  id?: number;
  titulo: string;
  resumo: string;
  conteudo: string;
  tipo: AvisoAtualizacaoTipoVM;
  status: AvisoAtualizacaoStatusVM;
  prioridade: AvisoAtualizacaoPrioridadeVM;
  inicioExibicao: Date | string | null;
  fimExibicao?: Date | string | null;
  textoAcao?: string;
  linkAcao?: string;
  exigirConfirmacao?: boolean;
  exibirUmaVezPorUsuario?: boolean;
  global?: boolean;
  empresaIds?: number[];
  totalVisualizacoes?: number;
  totalConfirmacoes?: number;
  totalAdiamentos?: number;
  totalExibicoes?: number;
}

export interface AvisoAtualizacaoResultadoListagemVM {
  data: AvisoAtualizacaoResumoVM[];
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class AvisosAtualizacaoService extends ServerService {
  private endpoint = '/avisos-atualizacao';

  constructor(http: HttpClient) {
    super(http);
  }

  liste(filtro: AvisoAtualizacaoFiltroListagemVM = {}): Promise<AvisoAtualizacaoResultadoListagemVM> {
    const params: any = this.removaUndefined({
      busca: filtro.busca || undefined,
      status: filtro.status || undefined,
      inicio: filtro.inicio != null ? filtro.inicio : undefined,
      quantidade: filtro.quantidade != null ? filtro.quantidade : undefined
    });

    return this.obtenha(`${this.endpoint}/liste`, params).then((retorno: any) => ({
      data: (retorno?.data || []).map((item: any) => this.mapearResumoDaApi(item)),
      total: Number(retorno?.total || 0)
    }));
  }

  obtenhaPorId(id: number): Promise<AvisoAtualizacaoDetalheVM> {
    return this.obtenha(`${this.endpoint}/${id}`, {}).then((dados: any) => this.mapearDetalheDaApi(dados));
  }

  salveAviso(aviso: AvisoAtualizacaoEdicaoVM): Promise<number> {
    const payload = this.mapearParaApi(aviso);

    if (aviso && aviso.id) {
      return this.facaPut(`${this.endpoint}/${aviso.id}`, payload).then((retorno: any) => Number(retorno?.id || aviso.id));
    }

    return this.facaPost(this.endpoint, payload).then((retorno: any) => Number(retorno?.id || 0));
  }

  publique(id: number): Promise<number> {
    return this.facaPost(`${this.endpoint}/${id}/publicar`, {}).then((retorno: any) => Number(retorno?.id || id));
  }

  arquive(id: number): Promise<number> {
    return this.facaPost(`${this.endpoint}/${id}/arquivar`, {}).then((retorno: any) => Number(retorno?.id || id));
  }

  obtenhaAtivos(): Promise<AvisoAtualizacaoAtivoVM[]> {
    return this.obtenha(`${this.endpoint}/ativos`, {}).then((retorno: any) =>
      (retorno?.data || []).map((item: any) => this.mapearAtivoDaApi(item))
    );
  }

  marqueVisualizado(id: number): Promise<number> {
    return this.facaPost(`${this.endpoint}/${id}/visualizado`, {}).then((retorno: any) => Number(retorno?.id || id));
  }

  marqueConfirmado(id: number): Promise<number> {
    return this.facaPost(`${this.endpoint}/${id}/confirmado`, {}).then((retorno: any) => Number(retorno?.id || id));
  }

  marqueAdiado(id: number): Promise<number> {
    return this.facaPost(`${this.endpoint}/${id}/adiado`, {}).then((retorno: any) => Number(retorno?.id || id));
  }

  private mapearResumoDaApi(dados: any): AvisoAtualizacaoResumoVM {
    return {
      id: Number(dados?.id || 0),
      titulo: this.normalizarTexto(dados?.titulo),
      resumo: this.normalizarTexto(dados?.resumo),
      tipo: this.normalizarTipo(dados?.tipo),
      status: this.normalizarStatus(dados?.status),
      prioridade: this.normalizarPrioridade(dados?.prioridade),
      inicioExibicao: this.normalizarData(dados?.inicioExibicao),
      fimExibicao: this.normalizarData(dados?.fimExibicao),
      global: this.normalizarBooleano(dados?.global, true),
      exigirConfirmacao: this.normalizarBooleano(dados?.exigirConfirmacao, false),
      exibirUmaVezPorUsuario: this.normalizarBooleano(dados?.exibirUmaVezPorUsuario, true),
      empresaIds: this.normalizarIds(dados?.empresaIds),
      criadoEm: this.normalizarData(dados?.criadoEm),
      alteradoEm: this.normalizarData(dados?.alteradoEm),
      totalVisualizacoes: Number(dados?.totalVisualizacoes || 0),
      totalConfirmacoes: Number(dados?.totalConfirmacoes || 0),
      totalAdiamentos: Number(dados?.totalAdiamentos || 0),
      totalExibicoes: Number(dados?.totalExibicoes || 0)
    };
  }

  private mapearDetalheDaApi(dados: any): AvisoAtualizacaoDetalheVM {
    const resumo = this.mapearResumoDaApi(dados);

    return {
      ...resumo,
      imagens: this.mapearImagens(dados?.imagens),
      conteudo: this.normalizarTexto(dados?.conteudo),
      textoAcao: this.normalizarTexto(dados?.textoAcao),
      linkAcao: this.normalizarTexto(dados?.linkAcao),
      usuarioCriacaoId: this.normalizarNumero(dados?.usuarioCriacaoId),
      usuarioAlteracaoId: this.normalizarNumero(dados?.usuarioAlteracaoId)
    };
  }

  private mapearAtivoDaApi(dados: any): AvisoAtualizacaoAtivoVM {
    return {
      imagens: this.mapearImagens(dados?.imagens),
      id: Number(dados?.id || 0),
      titulo: this.normalizarTexto(dados?.titulo),
      resumo: this.normalizarTexto(dados?.resumo),
      conteudo: this.normalizarTexto(dados?.conteudo),
      tipo: this.normalizarTipo(dados?.tipo),
      prioridade: this.normalizarPrioridade(dados?.prioridade),
      inicioExibicao: this.normalizarData(dados?.inicioExibicao),
      fimExibicao: this.normalizarData(dados?.fimExibicao),
      textoAcao: this.normalizarTexto(dados?.textoAcao),
      linkAcao: this.normalizarTexto(dados?.linkAcao),
      exigirConfirmacao: this.normalizarBooleano(dados?.exigirConfirmacao, false),
      exibirUmaVezPorUsuario: this.normalizarBooleano(dados?.exibirUmaVezPorUsuario, true),
      global: this.normalizarBooleano(dados?.global, true),
      empresaIds: this.normalizarIds(dados?.empresaIds)
    };
  }

  private mapearParaApi(aviso: AvisoAtualizacaoEdicaoVM): any {
    return this.removaUndefined({
      imagens: aviso?.imagens === undefined ? undefined : this.mapearImagens(aviso.imagens),
      id: aviso?.id,
      titulo: this.normalizarTexto(aviso?.titulo),
      resumo: this.normalizarTexto(aviso?.resumo),
      conteudo: this.normalizarTexto(aviso?.conteudo),
      tipo: this.normalizarTipo(aviso?.tipo),
      status: this.normalizarStatus(aviso?.status),
      prioridade: this.normalizarPrioridade(aviso?.prioridade),
      inicioExibicao: this.normalizarData(aviso?.inicioExibicao),
      fimExibicao: this.normalizarData(aviso?.fimExibicao),
      textoAcao: this.normalizarTexto(aviso?.textoAcao),
      linkAcao: this.normalizarTexto(aviso?.linkAcao),
      exigirConfirmacao: this.normalizarBooleano(aviso?.exigirConfirmacao, false),
      exibirUmaVezPorUsuario: this.normalizarBooleano(aviso?.exibirUmaVezPorUsuario, true),
      global: this.normalizarBooleano(aviso?.global, true),
      empresaIds: this.normalizarIds(aviso?.empresaIds)
    });
  }

  private mapearImagens(imagens: any): ImagemAvisoAtualizacaoVM[] {
    return (Array.isArray(imagens) ? imagens : []).map((imagem, ordem) => ({
      linkImagem: String(imagem.linkImagem || ''), ordem
    }));
  }

  private normalizarTexto(valor: any): string {
    return String(valor || '').trim();
  }

  private normalizarData(valor: any): Date | null {
    if (!valor) {
      return null;
    }

    const data = valor instanceof Date ? valor : new Date(valor);

    if (Number.isNaN(data.getTime())) {
      return null;
    }

    return data;
  }

  private normalizarBooleano(valor: any, valorPadrao: boolean): boolean {
    if (valor == null || valor === '') {
      return valorPadrao;
    }

    if (typeof valor === 'boolean') {
      return valor;
    }

    if (typeof valor === 'number') {
      return valor === 1;
    }

    const texto = String(valor).trim().toLowerCase();
    return texto === 'true' || texto === '1' || texto === 'sim';
  }

  private normalizarIds(valor: any): number[] {
    if (!Array.isArray(valor)) {
      return [];
    }

    return valor
      .map((item: any) => Number(item))
      .filter((item: number) => !Number.isNaN(item) && item > 0)
      .filter((item: number, index: number, itens: number[]) => itens.indexOf(item) === index);
  }

  private normalizarNumero(valor: any): number | null {
    const numero = Number(valor);
    return Number.isNaN(numero) || numero <= 0 ? null : numero;
  }

  private normalizarTipo(valor: any): AvisoAtualizacaoTipoVM {
    if (
      valor === 'novidade' ||
      valor === 'melhoria' ||
      valor === 'correcao' ||
      valor === 'comunicado_operacional' ||
      valor === 'indisponibilidade_suporte' ||
      valor === 'manutencao' ||
      valor === 'importante'
    ) {
      return valor;
    }

    return 'comunicado_operacional';
  }

  private normalizarStatus(valor: any): AvisoAtualizacaoStatusVM {
    if (valor === 'publicado' || valor === 'arquivado') {
      return valor;
    }

    return 'rascunho';
  }

  private normalizarPrioridade(valor: any): AvisoAtualizacaoPrioridadeVM {
    if (valor === 'alta' || valor === 'baixa') {
      return valor;
    }

    return 'normal';
  }

  private removaUndefined(obj: any): any {
    Object.keys(obj || {}).forEach((chave: string) => {
      if (obj[chave] === undefined || obj[chave] === null) {
        delete obj[chave];
      }
    });

    return obj;
  }
}
